/*
 * WebGL badge engine — TypeScript port of the prototype's `docs/nope-badges/nope-badges.js`
 * (three@0.128.0, pinned: newer three changed its colour management API).
 *
 * One shared renderer draws every badge into its own 2D canvas. Textures (albedo, normal,
 * roughness/metalness) are baked from the SVG masks of `src/common/badge-art`; the body is an
 * extruded, bevelled metal shape with a clearcoated enamel face; special badges get a cut
 * crystal dome that refracts the badge behind it. Loaded lazily by badge-art-mount.ts.
 */
import * as THREE from 'three'

import {
    BADGE_ASPECT,
    BADGE_TIER_STYLES,
    BadgeArtSpec,
    badgeArtVersion,
    badgeNumeralSpec,
    renderBadgeMasks,
} from '../../../common/badge-art/index.js'
import { BadgeGlHandle } from './badge-gl-registry.js'

const FONT = "'Hanken Grotesk', Arial, Helvetica, sans-serif"
const TEX_MAX = 24
const GROUP_MAX = 12
const TEX_SIZE = 512
/** Supersampling of the shared renderer relative to the target canvas. */
const SS = 2

interface Baked {
    albedo: HTMLCanvasElement
    normal: HTMLCanvasElement
    orm: HTMLCanvasElement
    /** Outline samples on the 200 grid (extrusion + crystal dome). */
    pts: [number, number][]
    metal: string
    crystal: boolean
    ic: [number, number, number]
}

interface CrystalUniforms {
    tBack: { value: THREE.Texture | null }
    uRes: { value: THREE.Vector2 }
}

interface GlState {
    ok: boolean | null
    R: THREE.WebGLRenderer | null
    scene: THREE.Scene | null
    cam: THREE.PerspectiveCamera | null
    tex: Map<string, Baked>
    groups: Map<string, THREE.Group>
    mp: SVGPathElement | null
    cu: CrystalUniforms
    rt: THREE.WebGLRenderTarget | null
    /** True between webglcontextlost and webglcontextrestored: no frame can be drawn. */
    lost: boolean
}

const GL: GlState = {
    ok: null,
    R: null,
    scene: null,
    cam: null,
    tex: new Map(),
    groups: new Map(),
    mp: null,
    cu: { tBack: { value: null }, uRes: { value: new THREE.Vector2(1, 1) } },
    rt: null,
    lost: false,
}

const reduceMotion = () =>
    typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches

/** True when the browser can run the 3D renderer (creates the shared context on first call). */
export const webglAvailable = (): boolean => {
    if (GL.ok === null) glInit()
    return GL.ok === true
}

const glInit = () => {
    try {
        const R = new THREE.WebGLRenderer({
            antialias: true,
            alpha: true,
            preserveDrawingBuffer: true,
        })
        R.setPixelRatio(1)
        R.setClearColor(0x000000, 0)
        R.outputEncoding = THREE.sRGBEncoding
        R.toneMapping = THREE.ACESFilmicToneMapping
        R.toneMappingExposure = 1.12
        // Studio: a dark box with soft panels, baked into a PMREM environment map.
        const env = new THREE.Scene()
        env.add(
            new THREE.Mesh(
                new THREE.BoxGeometry(14, 14, 14),
                new THREE.MeshBasicMaterial({
                    color: new THREE.Color(0.05, 0.05, 0.056),
                    side: THREE.BackSide,
                }),
            ),
        )
        const panel = (
            w: number,
            h: number,
            x: number,
            y: number,
            z: number,
            intensity: number,
            tint = 0xffffff,
        ) => {
            const c = new THREE.Color(tint)
            c.multiplyScalar(intensity)
            const m = new THREE.Mesh(
                new THREE.PlaneGeometry(w, h),
                new THREE.MeshBasicMaterial({ color: c, side: THREE.DoubleSide }),
            )
            m.position.set(x, y, z)
            m.lookAt(0, 0, 0)
            env.add(m)
        }
        panel(8.5, 3.4, 0, 2.1, 6.3, 1.9) // front softbox (behind camera)
        panel(8.5, 2.6, 0, -1.7, 6.3, 0.42, 0xfff4ea) // low warm fill
        panel(5.5, 3.2, -2.6, 3.4, 4.6, 4.2) // key, top left
        panel(1.4, 5.5, 5.2, 0.6, 2.6, 2.4) // strip, right
        panel(9, 0.9, 0, 6.2, 0.5, 2.8) // top strip
        panel(2.6, 1.6, 2.2, -3.6, 4.4, 0.55, 0xfff1e0)
        panel(3.5, 1.2, -4.8, -1.2, 3, 0.9)
        const pm = new THREE.PMREMGenerator(R)
        const scene = new THREE.Scene()
        scene.environment = pm.fromScene(env, 0.035).texture
        // The studio only had to be baked once; free its GPU side (CR finding).
        pm.dispose()
        env.traverse((o) => {
            const mesh = o as THREE.Mesh
            if (mesh.geometry) mesh.geometry.dispose()
            if (mesh.material) (mesh.material as THREE.Material).dispose()
        })
        // A backgrounded tab or a GPU reset can drop the shared context. Until it is back
        // nothing can be drawn: badges show their SVG again and re-bake when restored.
        R.domElement.addEventListener('webglcontextlost', (e) => {
            e.preventDefault()
            GL.lost = true
            dropCaches()
            LIVE.forEach((h) => h.stale())
        })
        R.domElement.addEventListener('webglcontextrestored', () => {
            GL.lost = false
            LIVE.forEach((h) => h.invalidate())
        })
        const cam = new THREE.PerspectiveCamera(16, 228 / 236, 1, 40)
        cam.position.set(0, -0.06, 8.39)
        cam.lookAt(0, -0.06, 0)
        Object.assign(GL, { ok: true, R, scene, cam })
    } catch {
        GL.ok = false
    }
}

/** Hidden <path> used to sample the outline (getPointAtLength). */
const pathEl = (): SVGPathElement => {
    if (GL.mp?.isConnected) return GL.mp
    const ns = 'http://www.w3.org/2000/svg'
    const hs = document.createElementNS(ns, 'svg')
    hs.setAttribute('width', '0')
    hs.setAttribute('height', '0')
    hs.setAttribute('aria-hidden', 'true')
    hs.style.cssText = 'position:absolute;width:0;height:0;overflow:hidden'
    GL.mp = document.createElementNS(ns, 'path')
    hs.appendChild(GL.mp)
    document.body.appendChild(hs)
    return GL.mp
}

const loadImg = (svg: string): Promise<HTMLImageElement> =>
    new Promise((res, rej) => {
        const im = new Image()
        im.onload = () => res(im)
        im.onerror = rej
        im.src = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg)
    })

const cnv = (size: number): HTMLCanvasElement => {
    const c = document.createElement('canvas')
    c.width = c.height = size
    return c
}

const ctx2d = (c: HTMLCanvasElement): CanvasRenderingContext2D => {
    const x = c.getContext('2d')
    if (!x) throw new Error('badge-gl: no 2d context')
    return x
}

const alphaOf = (c: HTMLCanvasElement): Float32Array => {
    const d = ctx2d(c).getImageData(0, 0, c.width, c.height).data
    const a = new Float32Array(c.width * c.height)
    for (let i = 0; i < a.length; i++) a[i] = d[i * 4 + 3] / 255
    return a
}

/** Three-pass box blur (≈ gaussian) on a float alpha plane. */
const boxBlur = (src: Float32Array, W: number, H: number, radius: number): Float32Array => {
    const r = Math.max(1, Math.round(radius))
    const a = src.slice()
    const b = new Float32Array(a.length)
    const n = 2 * r + 1
    for (let it = 0; it < 3; it++) {
        for (let y = 0; y < H; y++) {
            const row = y * W
            let acc = 0
            for (let x = -r; x <= r; x++) acc += a[row + Math.min(W - 1, Math.max(0, x))]
            for (let x = 0; x < W; x++) {
                b[row + x] = acc / n
                acc += a[row + Math.min(W - 1, x + r + 1)] - a[row + Math.max(0, x - r)]
            }
        }
        for (let x = 0; x < W; x++) {
            let acc = 0
            for (let y = -r; y <= r; y++) acc += b[Math.min(H - 1, Math.max(0, y)) * W + x]
            for (let y = 0; y < H; y++) {
                a[y * W + x] = acc / n
                acc += b[Math.min(H - 1, y + r + 1) * W + x] - b[Math.max(0, y - r) * W + x]
            }
        }
    }
    return a
}

const hexRGB = (h: string): [number, number, number] => [
    parseInt(h.slice(1, 3), 16),
    parseInt(h.slice(3, 5), 16),
    parseInt(h.slice(5, 7), 16),
]

let fontReady: Promise<unknown> | null = null

/** Bake albedo, normal and roughness/metalness maps from the SVG masks (LRU-cached by key). */
const makeTex = async (spec: BadgeArtSpec, key: string): Promise<Baked> => {
    const cached = GL.tex.get(key)
    if (cached) {
        GL.tex.delete(key)
        GL.tex.set(key, cached)
        return cached
    }
    const T = TEX_SIZE
    const masks = renderBadgeMasks(spec, T)
    const [shI, meI, enI] = await Promise.all([
        loadImg(masks.shape),
        loadImg(masks.metal),
        loadImg(masks.enamel),
    ])
    const shC = cnv(T)
    const meC = cnv(T)
    const enC = cnv(T)
    ctx2d(shC).drawImage(shI, 0, 0, T, T)
    ctx2d(enC).drawImage(enI, 0, 0, T, T)
    const mx = ctx2d(meC) as CanvasRenderingContext2D & { letterSpacing?: string }
    mx.drawImage(meI, 0, 0, T, T)
    if (masks.num !== null) {
        // Numerals are drawn on canvas so the page font (Hanken Grotesk 800) is used.
        const n = badgeNumeralSpec(masks.num)
        const sc = T / 200
        mx.textAlign = 'center'
        mx.textBaseline = 'alphabetic'
        mx.fillStyle = '#fff'
        const LS = 'letterSpacing' in mx
        mx.font = `800 ${n.fs * sc}px ${FONT}`
        if (LS) mx.letterSpacing = `${n.ls * sc}px`
        mx.fillText(String(masks.num), (100 + (LS ? n.ls / 2 : 0)) * sc, (n.cy + n.fs * 0.36) * sc)
        if (LS) mx.letterSpacing = '0px'
    }
    const W = T
    const N = W * W
    const M = alphaOf(meC)
    const Sh = alphaOf(shC)
    const b1 = boxBlur(M, W, W, Math.max(2, T / 180))
    const b2 = boxBlur(M, W, W, T / 70)
    // Height field: metal parts sit proud of the enamel with a soft shoulder.
    const Hh = new Float32Array(N)
    for (let i = 0; i < N; i++) Hh[i] = Sh[i] * (0.06 + 0.64 * b1[i] + 0.3 * b2[i])
    const nC = cnv(T)
    const oC = cnv(T)
    const aC = cnv(T)
    const nd = ctx2d(nC).createImageData(W, W)
    const od = ctx2d(oC).createImageData(W, W)
    const ad = ctx2d(aC).createImageData(W, W)
    const ed = ctx2d(enC).getImageData(0, 0, W, W).data
    const metal = BADGE_TIER_STYLES[spec.tier].metal
    const mc = hexRGB(metal)
    const hmax = 6
    const du = (2 * 200) / T
    for (let y = 0; y < W; y++) {
        for (let x = 0; x < W; x++) {
            const i = y * W + x
            const m = M[i]
            const hl = Hh[y * W + Math.max(0, x - 1)]
            const hr = Hh[y * W + Math.min(W - 1, x + 1)]
            const hu = Hh[Math.max(0, y - 1) * W + x]
            const hd = Hh[Math.min(W - 1, y + 1) * W + x]
            let nx = (-(hr - hl) * hmax) / du
            let ny = ((hd - hu) * hmax) / du
            let nz = 1
            const l = Math.hypot(nx, ny, nz)
            nx /= l
            ny /= l
            nz /= l
            const o = i * 4
            nd.data[o] = (nx * 0.5 + 0.5) * 255
            nd.data[o + 1] = (ny * 0.5 + 0.5) * 255
            nd.data[o + 2] = (nz * 0.5 + 0.5) * 255
            nd.data[o + 3] = 255
            // ORM: roughness in G (enamel .6 → metal .16), metalness in B.
            od.data[o] = 255
            od.data[o + 1] = (0.6 - 0.44 * m) * 255
            od.data[o + 2] = m * 255
            od.data[o + 3] = 255
            const ao = 1 - 0.38 * Math.max(0, Math.min(1, b2[i] - m))
            for (let k = 0; k < 3; k++) ad.data[o + k] = ed[o + k] * ao * (1 - m) + mc[k] * m
            ad.data[o + 3] = 255
        }
    }
    ctx2d(nC).putImageData(nd, 0, 0)
    ctx2d(oC).putImageData(od, 0, 0)
    ctx2d(aC).putImageData(ad, 0, 0)
    const mp = pathEl()
    mp.setAttribute('d', masks.d)
    const L = mp.getTotalLength()
    const n = Math.min(420, Math.max(180, Math.round(L * 0.9)))
    const pts: [number, number][] = []
    for (let i = 0; i < n; i++) {
        const q = mp.getPointAtLength((L * i) / n)
        pts.push([q.x, q.y])
    }
    const e: Baked = {
        albedo: aC,
        normal: nC,
        orm: oC,
        pts,
        metal,
        crystal: spec.tier === 'crystal',
        ic: masks.iconCenter,
    }
    GL.tex.set(key, e)
    while (GL.tex.size > TEX_MAX) GL.tex.delete(GL.tex.keys().next().value!)
    return e
}

/** Iridescent thin-film tint on the metal of awarded badges. */
const irid = <M extends THREE.Material>(m: M): M => {
    m.onBeforeCompile = (sh) => {
        sh.fragmentShader = sh.fragmentShader.replace(
            '#include <lights_physical_fragment>',
            `#include <lights_physical_fragment>
{vec3 vd=normalize(vViewPosition);float ang=acos(clamp(dot(normal,vd),0.0,1.0));
vec3 film=0.5+0.5*cos(6.28318*(vec3(0.0,0.33,0.67)+ang*2.4+vViewPosition.x*0.42-vViewPosition.y*0.3+0.12));
film=mix(vec3(0.97,0.95,0.93),film,0.42);
material.specularColor=mix(material.specularColor,film,metalnessFactor);}`,
        )
    }
    m.customProgramCacheKey = () => 'nb-irid'
    return m
}

/** Cut crystal: samples the badge rendered behind it with refraction, dispersion and Fresnel. */
const crystalMat = (): THREE.MeshPhysicalMaterial => {
    const m = new THREE.MeshPhysicalMaterial({
        color: 0x000000,
        metalness: 0,
        roughness: 0.03,
        reflectivity: 1,
        flatShading: true,
        toneMapped: false,
    })
    m.onBeforeCompile = (sh) => {
        Object.assign(sh.uniforms, GL.cu)
        sh.vertexShader =
            'varying vec3 vObj;\n' +
            sh.vertexShader.replace(
                '#include <begin_vertex>',
                '#include <begin_vertex>\nvObj=position;',
            )
        sh.fragmentShader =
            'uniform sampler2D tBack;uniform vec2 uRes;varying vec3 vObj;\n' +
            sh.fragmentShader
                .replace(
                    '#include <emissivemap_fragment>',
                    `{vec3 V=normalize(vViewPosition);vec3 I=-V;
vec3 No=normalize(cross(dFdx(vObj),dFdy(vObj)));
float h=fract(sin(dot(floor(No.xy*37.0+0.5),vec2(12.9898,78.233)))*43758.5453);
vec2 dev=(refract(I,normal,0.625)-I).xy;dev*=mix(1.0,-1.7,step(0.78,h));
vec2 s=gl_FragCoord.xy/uRes;float K=0.11;vec3 tr;
tr.r=texture2D(tBack,s+dev*K*0.94).r;tr.g=texture2D(tBack,s+dev*K).g;tr.b=texture2D(tBack,s+dev*K*1.08).b;
tr=sRGBToLinear(vec4(tr,1.0)).rgb;
float tb=smoothstep(0.985,0.999,abs(No.z));float shade=mix(mix(0.62,1.0,h),0.98,tb);
float F=0.16+0.84*pow(1.0-clamp(dot(normal,V),0.0,1.0),5.0);
totalEmissiveRadiance=tr*shade*vec3(0.98,1.03,1.1)*(1.0-F);}`,
                )
                .replace(
                    '#include <lights_physical_fragment>',
                    `#include <lights_physical_fragment>
{vec3 vd=normalize(vViewPosition);float ang=acos(clamp(dot(normal,vd),0.0,1.0));
vec3 film=0.5+0.5*cos(6.28318*(vec3(0.0,0.33,0.67)+ang*2.2+vObj.x*0.9-vObj.y*0.7));
material.specularColor*=mix(vec3(1.0),film*1.5,0.5);}`,
                )
    }
    m.customProgramCacheKey = () => 'nb-crystal'
    return m
}

/** Faceted dome over the face: three rings of the outline stepping up to the icon centre. */
const crystalGeo = (
    pts: [number, number][],
    c: [number, number, number],
    z0: number,
): THREE.BufferGeometry => {
    const n = pts.length
    const N = 28
    const P: [number, number][] = []
    for (let k = 0; k < N; k++) P.push(pts[Math.floor((k * n) / N)])
    const Wd = (p: [number, number]): [number, number] => [(p[0] - 100) / 100, -(p[1] - 100) / 100]
    const C = Wd([c[0], c[1]])
    const ring = (sc: number, z: number, off: boolean): [number, number, number][] =>
        P.map((p, k) => {
            let q = p
            if (off) {
                const p2 = P[(k + 1) % N]
                q = [(p[0] + p2[0]) / 2, (p[1] + p2[1]) / 2]
            }
            const w = Wd(q)
            return [C[0] + (w[0] - C[0]) * sc, C[1] + (w[1] - C[1]) * sc, z]
        })
    const A = ring(0.86, z0 + 0.004, false)
    const B = ring(0.72, z0 + 0.075, true)
    const Cc = ring(0.56, z0 + 0.105, false)
    const top: [number, number, number] = [C[0], C[1], z0 + 0.105]
    const v: number[] = []
    const tri = (
        a: [number, number, number],
        b2: [number, number, number],
        c2: [number, number, number],
    ) => {
        const ux = b2[0] - a[0]
        const uy = b2[1] - a[1]
        const vx = c2[0] - a[0]
        const vy = c2[1] - a[1]
        if (ux * vy - uy * vx < 0) {
            const t = b2
            b2 = c2
            c2 = t
        }
        v.push(...a, ...b2, ...c2)
    }
    for (let k = 0; k < N; k++) {
        const k1 = (k + 1) % N
        tri(A[k], A[k1], B[k])
        tri(A[k1], B[k1], B[k])
        tri(B[k], B[k1], Cc[k1])
        tri(B[k], Cc[k1], Cc[k])
        tri(top, Cc[k], Cc[k1])
    }
    const g = new THREE.BufferGeometry()
    g.setAttribute('position', new THREE.Float32BufferAttribute(v, 3))
    g.computeVertexNormals()
    return g
}

const disposeGroup = (g: THREE.Group) => {
    g.traverse((o) => {
        const mesh = o as THREE.Mesh
        if (mesh.geometry) mesh.geometry.dispose()
        const mat = mesh.material as THREE.MeshPhysicalMaterial | undefined
        if (mat) {
            for (const t of [
                mat.map,
                mat.normalMap,
                mat.roughnessMap,
                mat.metalnessMap,
                mat.clearcoatNormalMap,
            ]) {
                if (t) t.dispose()
            }
            mat.dispose()
        }
    })
}

/** Body + face (+ crystal dome) for a baked badge (LRU-cached by key). */
const groupFor = (key: string, e: Baked): THREE.Group => {
    const cached = GL.groups.get(key)
    if (cached) {
        GL.groups.delete(key)
        GL.groups.set(key, cached)
        return cached
    }
    const shape = new THREE.Shape()
    e.pts.forEach((p, i) => {
        const x = (p[0] - 100) / 100
        const y = -(p[1] - 100) / 100
        if (i) shape.lineTo(x, y)
        else shape.moveTo(x, y)
    })
    const depth = 0.05
    const bt = 0.035
    const body = new THREE.ExtrudeGeometry(shape, {
        depth,
        bevelEnabled: true,
        bevelThickness: bt,
        bevelSize: 0.026,
        bevelSegments: 5,
        curveSegments: 4,
    })
    const bodyM = new THREE.MeshPhysicalMaterial({
        color: new THREE.Color(e.metal).convertSRGBToLinear(),
        metalness: 1,
        roughness: 0.24,
        clearcoat: 0.5,
        clearcoatRoughness: 0.1,
    })
    const face = new THREE.ShapeGeometry(shape)
    const pos = face.attributes.position
    const uv = face.attributes.uv as THREE.BufferAttribute
    for (let i = 0; i < pos.count; i++) {
        uv.setXY(i, (pos.getX(i) * 100 + 100) / 200, (pos.getY(i) * 100 + 100) / 200)
    }
    uv.needsUpdate = true
    const tx = (c: HTMLCanvasElement, srgb = false) => {
        const t = new THREE.CanvasTexture(c)
        if (srgb) t.encoding = THREE.sRGBEncoding
        t.anisotropy = 4
        return t
    }
    const nt = tx(e.normal)
    const ot = tx(e.orm)
    const at = tx(e.albedo, true)
    const faceM = new THREE.MeshPhysicalMaterial({
        map: at,
        normalMap: nt,
        normalScale: new THREE.Vector2(1, 1),
        roughnessMap: ot,
        metalnessMap: ot,
        metalness: 1,
        roughness: 1,
        clearcoat: 1,
        clearcoatRoughness: 0.07,
        clearcoatNormalMap: nt,
    })
    if (e.crystal) {
        irid(bodyM)
        irid(faceM)
    }
    const g = new THREE.Group()
    const fm = new THREE.Mesh(face, faceM)
    fm.position.z = depth + bt + 0.0006
    g.add(new THREE.Mesh(body, bodyM), fm)
    if (e.crystal) {
        const dm = new THREE.Mesh(crystalGeo(e.pts, e.ic, depth + bt + 0.0012), crystalMat())
        g.add(dm)
        g.userData.dome = dm
    }
    GL.groups.set(key, g)
    while (GL.groups.size > GROUP_MAX) {
        const first = GL.groups.entries().next().value!
        GL.groups.delete(first[0])
        disposeGroup(first[1])
    }
    return g
}

/** Render one frame into a 2D canvas. False when the textures were evicted (prepare again). */
const drawFrame = (cv: HTMLCanvasElement, key: string, rx: number, ry: number): boolean => {
    if (!GL.ok || GL.lost || !GL.R || !GL.scene || !GL.cam) return false
    let g = GL.groups.get(key)
    if (!g) {
        const e = GL.tex.get(key)
        if (!e) return false
        g = groupFor(key, e)
    }
    const W = cv.width
    const H = cv.height
    const w = W * SS
    const h = H * SS
    GL.R.setSize(w, h, false)
    GL.cam.aspect = W / H
    GL.cam.updateProjectionMatrix()
    g.rotation.set(rx, ry, 0)
    GL.scene.add(g)
    const d = g.userData.dome as THREE.Mesh | undefined
    if (d) {
        // Pass 1: the badge without its crystal, into a texture the crystal refracts.
        if (GL.rt?.width !== w || GL.rt.height !== h) {
            if (GL.rt) GL.rt.dispose()
            GL.rt = new THREE.WebGLRenderTarget(w, h, {
                minFilter: THREE.LinearFilter,
                magFilter: THREE.LinearFilter,
                format: THREE.RGBAFormat,
            })
            GL.rt.texture.encoding = THREE.sRGBEncoding
            GL.rt.texture.generateMipmaps = false
        }
        d.visible = false
        GL.R.setRenderTarget(GL.rt)
        GL.R.clear()
        GL.R.render(GL.scene, GL.cam)
        GL.R.setRenderTarget(null)
        d.visible = true
        GL.cu.tBack.value = GL.rt.texture
        GL.cu.uRes.value.set(w, h)
    }
    GL.R.render(GL.scene, GL.cam)
    GL.scene.remove(g)
    const x = ctx2d(cv)
    x.imageSmoothingEnabled = true
    x.imageSmoothingQuality = 'high'
    x.clearRect(0, 0, W, H)
    x.drawImage(GL.R.domElement, 0, 0, w, h, 0, 0, W, H)
    return true
}

const prepare = async (spec: BadgeArtSpec, key: string): Promise<string> => {
    if (spec.anniversaryYears !== null && document.fonts?.load) {
        fontReady = fontReady || document.fonts.load(`800 60px ${FONT}`).catch(() => undefined)
        await fontReady
    }
    await makeTex(spec, key)
    return key
}

/** Bakes are serialised: one at a time keeps the main thread responsive. */
let QUEUE: Promise<unknown> = Promise.resolve()
const enqueue = (fn: () => Promise<void>): void => {
    QUEUE = QUEUE.then(fn).catch((err) => console.warn('badge-gl:', err))
}

const wait = (ms: number) => new Promise((r) => setTimeout(r, ms))

export interface BadgeGlOptions {
    /** Called after each successful (re)draw: the SVG placeholder can be hidden. */
    onReady: () => void
    /** Called when the canvas content is no longer valid: show the SVG again. */
    onStale: () => void
}

/** Every mounted badge, so a context loss/restore can reach all of them. */
const LIVE = new Set<BadgeGlHandle>()

/** Throw away everything baked for the lost context; groups hold GPU textures. */
const dropCaches = () => {
    GL.groups.forEach((g) => disposeGroup(g))
    GL.groups.clear()
    GL.tex.clear()
    if (GL.rt) {
        GL.rt.dispose()
        GL.rt = null
    }
}

/**
 * Drive one badge canvas. The canvas fills its parent (`.badge-art`); call `show()` when it
 * scrolls into view and `invalidate()` when its width changes.
 */
export const createBadgeGl = (
    cv: HTMLCanvasElement,
    spec: BadgeArtSpec,
    opts: BadgeGlOptions,
): BadgeGlHandle => {
    const key = badgeArtVersion(spec)
    const st = { rx: 0, ry: 0, tx: 0, ty: 0, raf: 0 }
    let visible = false
    let dirty = true
    /** A draw is already waiting in the bake queue (coalesces resize bursts, CR finding). */
    let queued = false
    let busy = false
    let ready = false
    let disposed = false

    /** The canvas no longer shows a valid frame (context lost, textures evicted): fall back to the SVG. */
    const stale = () => {
        if (!ready) return
        ready = false
        dirty = true
        opts.onStale()
    }
    const draw = async () => {
        queued = false
        if (disposed) return
        const r = cv.getBoundingClientRect()
        if (r.width < 4) {
            dirty = true
            return
        }
        const dpr = Math.min(2, window.devicePixelRatio || 1)
        const W = Math.round(r.width * dpr)
        const H = Math.round(W * BADGE_ASPECT)
        if (cv.width !== W) cv.width = W
        if (cv.height !== H) cv.height = H
        if (GL.lost) {
            dirty = true
            return
        }
        await prepare(spec, key)
        if (disposed) return
        if (drawFrame(cv, key, st.rx, st.ry)) {
            ready = true
            opts.onReady()
        } else {
            dirty = true
            stale()
        }
    }
    const schedule = () => {
        dirty = false
        if (queued) return
        queued = true
        enqueue(draw)
    }
    const loop = () => {
        if (st.raf) return
        const step = () => {
            st.rx += (st.tx - st.rx) * 0.22
            st.ry += (st.ty - st.ry) * 0.22
            if (!drawFrame(cv, key, st.rx, st.ry)) {
                st.raf = 0
                stale()
                if (!GL.lost) schedule()
                return
            }
            if (Math.abs(st.tx - st.rx) + Math.abs(st.ty - st.ry) > 0.0015) {
                st.raf = requestAnimationFrame(step)
            } else {
                st.raf = 0
                st.rx = st.tx
                st.ry = st.ty
                drawFrame(cv, key, st.rx, st.ry)
            }
        }
        st.raf = requestAnimationFrame(step)
    }

    const handle: BadgeGlHandle = {
        stale,
        show: () => {
            visible = true
            if (dirty) schedule()
        },
        hide: () => {
            visible = false
        },
        invalidate: () => {
            dirty = true
            if (visible) schedule()
        },
        tilt: (px, py) => {
            if (!ready || busy || reduceMotion()) return
            st.tx = (py - 0.5) * 0.5
            st.ty = (px - 0.5) * 0.62
            loop()
        },
        release: () => {
            st.tx = 0
            st.ty = 0
            if (ready && !busy) loop()
        },
        spin: async () => {
            if (!ready || busy || reduceMotion()) return
            busy = true
            if (st.raf) cancelAnimationFrame(st.raf)
            st.raf = 0
            await wait(0)
            const t0 = performance.now()
            await new Promise<void>((res) => {
                const step = (now: number) => {
                    if (disposed) return res()
                    const k = Math.min(1, (now - t0) / 1100)
                    const e = 1 - Math.pow(1 - k, 3)
                    drawFrame(
                        cv,
                        key,
                        0.12 * Math.sin(k * Math.PI) * (1 - k),
                        0.95 * (1 - e) * Math.cos(k * 6),
                    )
                    if (k < 1) requestAnimationFrame(step)
                    else res()
                }
                requestAnimationFrame(step)
            })
            st.rx = st.ry = st.tx = st.ty = 0
            if (!disposed) drawFrame(cv, key, 0, 0)
            busy = false
        },
        dispose: () => {
            disposed = true
            LIVE.delete(handle)
            if (st.raf) cancelAnimationFrame(st.raf)
            st.raf = 0
        },
    }
    LIVE.add(handle)
    return handle
}
